import {
    type DeepPartial,
    HolisticCategory,
    InvestmentManagementAccountType,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    MoneyMapRuleGoalType,
    type MoneyMapRuleWithAccountDetails,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
    Portfolio,
    RiskLevel,
    SupportedApexAccountRegistrationType,
    TimeHorizon,
} from '@fruitful/types';

export const PRESET_CORE_ACCOUNT_NAME = 'Core account';
export const PRESET_EMERGENCY_FUND_ACCOUNT_NAME = 'Emergency Fund';
export const PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME = 'Debt pay down';

export enum GoalPreset {
    ONE_MONTH_EMERGENCY_FUND = 'ONE_MONTH_EMERGENCY_FUND',
    FULLY_FUND_EMERGENCY_FUND = 'FULLY_FUND_EMERGENCY_FUND',
    PAY_OFF_DEBT = 'PAY_OFF_DEBT',
    BASELINE_RETIREMENT = 'BASELINE_RETIREMENT',
    APEX_BASELINE_RETIREMENT = 'APEX_BASELINE_RETIREMENT',
    BACKDOOR_ROTH = 'BACKDOOR_ROTH',
    // Backdoor Roth targeting the partner (second member) on a joint membership.
    BACKDOOR_ROTH_PARTNER = 'BACKDOOR_ROTH_PARTNER',
    SAVE_FOR_KIDS_COLLEGE = 'SAVE_FOR_KIDS_COLLEGE',
    SAVE_FOR_HOUSE = 'SAVE_FOR_HOUSE',
    SAVE_FOR_TRAVEL = 'SAVE_FOR_TRAVEL',
    SAVE_FOR_GIFTS = 'SAVE_FOR_GIFTS',
    CUSTOM = 'CUSTOM',
}

export const GoalPresetLabel: Record<GoalPreset, string> = {
    [GoalPreset.ONE_MONTH_EMERGENCY_FUND]: '1 month emergency fund',
    [GoalPreset.FULLY_FUND_EMERGENCY_FUND]: 'Fully funded emergency fund',
    [GoalPreset.PAY_OFF_DEBT]: 'Pay off debt',
    [GoalPreset.BASELINE_RETIREMENT]: 'Baseline retirement (Orion)',
    [GoalPreset.APEX_BASELINE_RETIREMENT]: 'New Investment Account (Apex)',
    [GoalPreset.BACKDOOR_ROTH]: 'Backdoor Roth',
    [GoalPreset.BACKDOOR_ROTH_PARTNER]: 'Backdoor Roth',
    [GoalPreset.SAVE_FOR_KIDS_COLLEGE]: "Save for kid's college",
    [GoalPreset.SAVE_FOR_HOUSE]: 'Save up for a house',
    [GoalPreset.SAVE_FOR_TRAVEL]: 'Save up for travel',
    [GoalPreset.SAVE_FOR_GIFTS]: 'Save up for gifts',
    [GoalPreset.CUSTOM]: 'Custom',
};

export const GoalPresetHelpText: Record<GoalPreset, string | undefined> = {
    [GoalPreset.ONE_MONTH_EMERGENCY_FUND]: undefined,
    [GoalPreset.FULLY_FUND_EMERGENCY_FUND]: undefined,
    [GoalPreset.PAY_OFF_DEBT]: 'ex. Pay off Chase credit card',
    [GoalPreset.BASELINE_RETIREMENT]:
        'Minimum post-paycheck retirement savings needed to retire at FRA',
    [GoalPreset.APEX_BASELINE_RETIREMENT]:
        'Minimum post-paycheck retirement savings needed to retire at FRA',
    [GoalPreset.BACKDOOR_ROTH]:
        'Contribute to a Traditional IRA, then convert to a Roth IRA',
    [GoalPreset.BACKDOOR_ROTH_PARTNER]:
        'Contribute to a Traditional IRA, then convert to a Roth IRA',
    [GoalPreset.SAVE_FOR_KIDS_COLLEGE]: 'Annual college savings per kid',
    [GoalPreset.SAVE_FOR_HOUSE]: undefined,
    [GoalPreset.SAVE_FOR_TRAVEL]: undefined,
    [GoalPreset.SAVE_FOR_GIFTS]: undefined,
    [GoalPreset.CUSTOM]: undefined,
};

const BACKDOOR_ROTH_RULE_CONFIG: DeepPartial<MoneyMapRuleWithAccountDetails> = {
    description: GoalPresetLabel[GoalPreset.BACKDOOR_ROTH],
    goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
    moneyMapAccount: {
        id: NEW_APEX_IM_ACCOUNT_OPTION.value,
        type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
        status: MoneyMapAccountStatus.PROPOSED,
        accountDetails: {
            name: 'Traditional IRA (backdoor)',
            apexAccountType:
                SupportedApexAccountRegistrationType.RegistrationIraTraditional,
        },
        backdoorRothLinkedMoneyMapAccount: {
            id: NEW_APEX_IM_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                // Superseded in GoalForm by `{firstName}'s Backdoor Roth IRA` (e.g. Bob's Backdoor Roth IRA).
                name: 'Backdoor Roth IRA',
                apexAccountType:
                    SupportedApexAccountRegistrationType.RegistrationIraRoth,
                apexPortfolio: Portfolio.CORE,
                apexRiskLevel: RiskLevel.TEN,
                apexTimeHorizon: TimeHorizon.AVERAGE,
            },
        },
    },
};

export const GoalPresetRuleConfigs: Record<
    GoalPreset,
    DeepPartial<MoneyMapRuleWithAccountDetails> | undefined
> = {
    [GoalPreset.ONE_MONTH_EMERGENCY_FUND]: {
        description: GoalPresetLabel[GoalPreset.ONE_MONTH_EMERGENCY_FUND],
        goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
        percentage: 1,
        moneyMapAccount: {
            id: NEW_CASH_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INTERNAL_BANKING,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                name: PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                holisticCategory: HolisticCategory.SAVE,
                hasPhysicalCard: false,
                hasVirtualCard: true,
            },
        },
    },
    [GoalPreset.FULLY_FUND_EMERGENCY_FUND]: {
        description: GoalPresetLabel[GoalPreset.FULLY_FUND_EMERGENCY_FUND],
        goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
        percentage: 1,
        moneyMapAccount: {
            id: NEW_CASH_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INTERNAL_BANKING,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                name: PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                holisticCategory: HolisticCategory.SAVE,
                hasPhysicalCard: false,
                hasVirtualCard: true,
            },
        },
    },
    [GoalPreset.PAY_OFF_DEBT]: {
        goalType: MoneyMapRuleGoalType.DEBT_PAY_DOWN,
        percentage: 1,
        moneyMapAccount: {
            moneyMapAccountForTransfer: {
                id: NEW_CASH_ACCOUNT_OPTION.value,
                type: MoneyMapAccountType.INTERNAL_BANKING,
                status: MoneyMapAccountStatus.PROPOSED,
                accountDetails: {
                    name: PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                    holisticCategory: HolisticCategory.SAVE,
                    hasPhysicalCard: false,
                    hasVirtualCard: true,
                },
            },
        },
    },
    [GoalPreset.BASELINE_RETIREMENT]: {
        goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
        moneyMapAccount: {
            id: NEW_IM_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INVESTMENT_MANAGEMENT,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                accountType: InvestmentManagementAccountType.ROTH_IRA,
                portfolio: Portfolio.CORE,
            },
            moneyMapAccountForTransfer: {
                id: NEW_CASH_ACCOUNT_OPTION.value,
                type: MoneyMapAccountType.INTERNAL_BANKING,
                status: MoneyMapAccountStatus.PROPOSED,
                accountDetails: {
                    name: PRESET_CORE_ACCOUNT_NAME,
                    holisticCategory: HolisticCategory.SPEND,
                    hasPhysicalCard: false,
                    hasVirtualCard: true,
                },
            },
        },
    },
    [GoalPreset.APEX_BASELINE_RETIREMENT]: {
        goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
        moneyMapAccount: {
            id: NEW_APEX_IM_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                apexAccountType:
                    SupportedApexAccountRegistrationType.RegistrationIndividual,
                apexPortfolio: Portfolio.CORE,
                apexRiskLevel: RiskLevel.ONE,
                apexTimeHorizon: TimeHorizon.AVERAGE,
            },
        },
    },
    // Primary and partner Backdoor Roth presets share the same rule shape; the owning
    // member is resolved in the form from the selected GoalPreset (see MoneyMapEditor).
    [GoalPreset.BACKDOOR_ROTH]: BACKDOOR_ROTH_RULE_CONFIG,
    [GoalPreset.BACKDOOR_ROTH_PARTNER]: BACKDOOR_ROTH_RULE_CONFIG,
    [GoalPreset.SAVE_FOR_KIDS_COLLEGE]: {
        goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
        moneyMapAccount: {
            moneyMapAccountForTransfer: {
                id: NEW_CASH_ACCOUNT_OPTION.value,
                type: MoneyMapAccountType.INTERNAL_BANKING,
                status: MoneyMapAccountStatus.PROPOSED,
                accountDetails: {
                    name: PRESET_CORE_ACCOUNT_NAME,
                    holisticCategory: HolisticCategory.SPEND,
                    hasPhysicalCard: false,
                    hasVirtualCard: true,
                },
            },
        },
    },
    [GoalPreset.SAVE_FOR_HOUSE]: {
        description: GoalPresetLabel[GoalPreset.SAVE_FOR_HOUSE],
        goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
        moneyMapAccount: {
            id: NEW_CASH_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INTERNAL_BANKING,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                name: 'House savings',
                holisticCategory: HolisticCategory.SAVE,
                hasPhysicalCard: false,
                hasVirtualCard: true,
            },
        },
    },
    [GoalPreset.SAVE_FOR_TRAVEL]: {
        description: GoalPresetLabel[GoalPreset.SAVE_FOR_TRAVEL],
        goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
        moneyMapAccount: {
            id: NEW_CASH_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INTERNAL_BANKING,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                name: 'Travel',
                holisticCategory: HolisticCategory.SAVE,
                hasPhysicalCard: false,
                hasVirtualCard: true,
            },
        },
    },
    [GoalPreset.SAVE_FOR_GIFTS]: {
        description: GoalPresetLabel[GoalPreset.SAVE_FOR_GIFTS],
        goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
        moneyMapAccount: {
            id: NEW_CASH_ACCOUNT_OPTION.value,
            type: MoneyMapAccountType.INTERNAL_BANKING,
            status: MoneyMapAccountStatus.PROPOSED,
            accountDetails: {
                name: 'Gifts',
                holisticCategory: HolisticCategory.SAVE,
                hasPhysicalCard: false,
                hasVirtualCard: true,
            },
        },
    },
    [GoalPreset.CUSTOM]: undefined,
};
